import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from "react-router-dom";
import Table from 'ui-components/table/table';
import { getInboundFreightsColumn } from './getInboundFreightsColumn';
import ContentContainer from 'templates/content-container/contentContainer';
import { useInboundFreightByTerminal } from '../hooks/useInboundFreightByTerminal';
import { getTerminalsList, terminalSummarySelector } from 'slices/terminal-summary/terminalSummarySlice';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from 'slices/user/userSlice';
import LabeledTextInput from 'ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import LabeledDateRangeInput from 'ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput';
import XGSSelect, { XGSSelectOption } from 'ui-components/xgs-select/xgsSelect';
import Button, { ButtonSizes, ButtonThemes } from 'ui-components/button/button';
import { inboundFreightStatusOptions } from 'app/data/freights/model';
import { ReactComponent as TotalTruck } from '../../../images/total_trucks.svg';
import { ReactComponent as Inbound } from '../../../images/truck_inbound.svg';
import { ReactComponent as Arrived } from '../../../images/arrived.svg';
import { ReactComponent as Late } from '../../../images/truck_late.svg';
import { ReactComponent as Alert } from '../../../images/alert.svg';
import { ReactComponent as Unloaded } from '../../../images/unloading.svg';
import { ReactComponent as Staged } from '../../../images/staged.svg';
import TerminalSummaryState from 'slices/terminal-summary/TerminalSummaryState';
import './inboundFreight.scss'
import { useInboundFreightCounts } from '../hooks/useInboundFreightCounts';
import Loading from 'ui-components/loading/loading';
import moment from 'moment';
import AssignDoorDrawer from './assignDoorDrawer';
import ArriveLoadModal from './arriveLoadModal';
import CloseDoorModal from './closeDoorModal';
import ManifestDetailsSlideIn from './manifestDetailsSlideIn';
import { apiQueryAdapter } from 'app/data/apiQueryAdapter';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from 'app/data/common/queryKeys';
import { Routes } from "app/route/RoutesConfig";
import { isInboundFreightAllowed } from "./allowedTerminals";

const Filters = ({
  searchString,
  setSearchString,
  status,
  setStatus,
  isLoading,
  setSubmittedSearch,
  terminalOptions,
  terminal,
  setTerminal,
  trailerSearch,
  setTrailerSearch,
  etaFrom,
  setEtaFrom,
  etaTo,
  setEtaTo,
  onSearch,
  onClear,
}: any) => {

  useEffect(() => {
    const handler =
      setTimeout(() =>
        setSubmittedSearch(searchString), 1000);
    return () => clearTimeout(handler);
  }, [searchString, setSubmittedSearch]);

  return (
    <div className="inbound-freight__filters">
      <div className="inbound-freight__filters__field inbound-freight__filters__field--eta">
        <LabeledDateRangeInput
          label="ETA Date Range:"
          labelMode={LabelModes.column}
          start={etaFrom}
          end={etaTo}
          onStartChange={(date) => setEtaFrom(date)}
          onEndChange={(date) => setEtaTo(date)}
          minDate={moment().subtract(1, "y").toDate()}
          className="inbound-freight__date-range"
        />
      </div>
      <div className="inbound-freight__filters__field inbound-freight__filters__field--select">
        <label>Freight Status:</label>
        <XGSSelect
          value={status}
          isLoading={isLoading}
          onValueChange={(v: XGSSelectOption | null | undefined) => {
            setStatus(v);
          }}
          placeholder="All Statuses"
          options={[{ label: "All Statuses", value: "" }, ...inboundFreightStatusOptions]}
          isClearable
        />
      </div>
      <div className="inbound-freight__filters__field">
        <LabeledTextInput
          label="Trailer:"
          labelMode={LabelModes.column}
          value={trailerSearch}
          onChange={(e) => setTrailerSearch(e.currentTarget.value)}
          placeholder="Search Trailer"
          clearable
          onClear={() => setTrailerSearch("")}
          onKeyDown={(e) => e.key === "Enter" && onSearch()}
        />
      </div>
      <div className="inbound-freight__filters__field">
        <LabeledTextInput
          label="Manifest:"
          labelMode={LabelModes.column}
          value={searchString}
          onChange={(e) => setSearchString(e.currentTarget.value)}
          placeholder="Search Manifest"
          clearable
          onClear={() => {
            setSearchString("");
            setSubmittedSearch("");
          }}
          onKeyDown={(e) => e.key === "Enter" && onSearch()}
        />
      </div>
      <div className="inbound-freight__filters__field inbound-freight__filters__field--select">
        <label>Origin Terminal:</label>
        <XGSSelect
          value={terminal}
          isLoading={isLoading}
          onValueChange={(v: XGSSelectOption | null | undefined) => {
            setTerminal(v);
          }}
          placeholder="All Terminals"
          options={[{ label: "All Terminals", value: "" }, ...terminalOptions]}
          isClearable
        />
      </div>
      <div className="inbound-freight__filters__actions">
        <Button theme={ButtonThemes.blue} size={ButtonSizes.medium} onClick={onSearch}>
          Search
        </Button>
        <Button theme={ButtonThemes.gray} size={ButtonSizes.medium} onClick={onClear}>
          Clear Filters
        </Button>
      </div>
    </div>
  );
}

const getTileIcon = (type: string) => {
  switch (type) {
    case "TOTAL_TRUCKS":
      return <TotalTruck />;
    case "LATE":
      return <Late />;
    case "ALERT":
      return <Alert />;
    case "INBOUND":
      return <Inbound />;
    case "ARRIVED":
      return <Arrived />;
    case "UNLOADED":
      return <Unloaded />;
    case "STAGED":
      return <Staged />;
    default:
      return null;
  }
};

const SummaryTiles = ({ isLoading, inboundFreightCounts }: any) => {
  const summaryTiles = [
    {
      title: "Total Trucks",
      count: inboundFreightCounts?.totalTrucks,
      iconType: "TOTAL_TRUCKS",
    },
    {
      title: "Late",
      count: inboundFreightCounts?.late,
      iconType: "LATE",
    },
    {
      title: "Alert",
      count:
        inboundFreightCounts?.alert ??
        inboundFreightCounts?.alerted ??
        inboundFreightCounts?.alerts ??
        0,
      iconType: "ALERT",
    },
    {
      title: "Inbound",
      count: inboundFreightCounts?.inbound,
      iconType: "INBOUND",
    },
    {
      title: "Arrived",
      count: inboundFreightCounts?.arrived,
      iconType: "ARRIVED",
    },
    {
      title: "Unloading",
      count: (inboundFreightCounts?.unloading ?? 0) + (inboundFreightCounts?.unloaded ?? 0),
      iconType: "UNLOADED",
    },
    {
      title: "Staged",
      count: inboundFreightCounts?.staged,
      iconType: "STAGED",
    },
  ];
  return (
    <div className="inbound-freights">
      {summaryTiles.map(({ title, count, iconType }) => (
        <div key={iconType} className="inbound-freights__tile">
          <div className={`icon-container --type-${iconType.toLowerCase()}`}>
            {getTileIcon(iconType)}
          </div>

          <div>
            {isLoading ? <Loading isLoading={true} /> : <div className="inbound-freights__tile__count">
              {count?.toLocaleString()}
            </div>}
            <div className="inbound-freights__tile__text">
              {title}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const InboundFreights: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const terminalSummaryState: TerminalSummaryState = useSelector(terminalSummarySelector);
  const userState = useSelector(userSelector);
  const canViewInboundFreight = isInboundFreightAllowed(userState.activeTerminal?.id);

  const [searchString, setSearchString] = useState<string>("");
  const [status, setStatus] = useState<XGSSelectOption | null>(null);
  const [terminal, setTerminal] = useState<XGSSelectOption | null>(null);
  const [terminalOptions, setTerminalOptions] = useState<{
    value: string;
    label: string;
  }[]>([]);
  const [submittedSearch, setSubmittedSearch] = useState<string>("");
  const [trailerSearch, setTrailerSearch] = useState<string>("");
  const [etaFrom, setEtaFrom] = useState<Date | undefined>(undefined);
  const [etaTo, setEtaTo] = useState<Date | undefined>(undefined);
  const [assignDoorDrawerOpen, setAssignDoorDrawerOpen] = useState(false);
  const [selectedFreightForDoor, setSelectedFreightForDoor] = useState<any>(null);
  const [arriveLoadModalOpen, setArriveLoadModalOpen] = useState(false);
  const [closeDoorModalOpen, setCloseDoorModalOpen] = useState(false);
  const [selectedFreightForAction, setSelectedFreightForAction] = useState<any>(null);
  const [isAssignDoorSaving, setIsAssignDoorSaving] = useState(false);
  const [isArriveLoadSubmitting, setIsArriveLoadSubmitting] = useState(false);
  const [isCloseDoorSubmitting, setIsCloseDoorSubmitting] = useState(false);
  const [manifestDetailsSlideInOpen, setManifestDetailsSlideInOpen] = useState(false);
  const [selectedFreightForManifest, setSelectedFreightForManifest] = useState<any>(null);
  const isAnyOverlayOpen = assignDoorDrawerOpen || arriveLoadModalOpen || closeDoorModalOpen || manifestDetailsSlideInOpen;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const manifestNumber = params.get("manifestNumber");
    if (manifestNumber != null && manifestNumber.trim() !== "") {
      setSearchString(manifestNumber.trim());
      setSubmittedSearch(manifestNumber.trim());
    }
  }, [location.search]);

  const { data: inboundFreightsRaw, isLoading } = useInboundFreightByTerminal(
    !terminal || terminal.value === "" ? null : terminal.label?.toString(),
    !status || status.value === "" ? null : status.value?.toString(),
    submittedSearch,
    trailerSearch,
    etaFrom ? moment(etaFrom).format('YYYY-MM-DD') : null,
    etaTo ? moment(etaTo).format('YYYY-MM-DD') : null
  );

  const inboundFreights = React.useMemo(
    () => inboundFreightsRaw ?? [],
    [inboundFreightsRaw]
  );

  const { data: inboundFreightCounts, isLoading: isLoadingInboundFreightCounts } = useInboundFreightCounts(
    submittedSearch,
    !terminal || terminal.value === "" ? undefined : terminal.label?.toString(),
    !status || status.value === "" ? undefined : status.value?.toString(),
    undefined
  );

  useEffect(() => {
    if (terminalSummaryState.terminallist) {
      const terminalOptions = terminalSummaryState.terminallist?.map((terminal: any) => ({
        value: terminal.terminalNumber.toString(),
        label: terminal.terminalName.toLowerCase().capitalize()
      })) || [];
      setTerminalOptions(terminalOptions);
    }
  }, [terminalSummaryState.terminallist]);

  useEffect(() => {
    dispatch(getTerminalsList(0, null, null, "ACTIVE", "XGS Terminal", true));
    // eslint-disable-next-line
  }, []);

  const handleSearch = () => {
    setSubmittedSearch(searchString);
  };

  const handleClear = () => {
    setSearchString("");
    setTerminal(null);
    setStatus(null);
    setTrailerSearch("");
    setEtaFrom(undefined);
    setEtaTo(undefined);
    setSubmittedSearch("");
  };

  const buildArriveLoadPayload = (values: any) => {
    const arrivalDate = values.arrivalDate
      ? moment(values.arrivalDate, "MM/DD/YYYY").format("YYYY-MM-DD")
      : undefined;
    const arrivalTime = values.arrivalTime
      ? values.arrivalTime.length === 5
        ? `${values.arrivalTime}:00`
        : values.arrivalTime
      : undefined;

    const payload: any = {
      arrivalDate,
      arrivalTime,
      freightType: "LINEHAUL",
      sealNumber: values.seal,
      checkedBy: values.checkedBy,
      sealCondition: values.sealCondition === "damaged" ? "DAMAGED" : "INTACT",
    };

    if (values.sealCondition === "damaged" && values.reasonForDamagedSeal) {
      payload.damagedSealReason = values.reasonForDamagedSeal;
    }

    return payload;
  };

  useEffect(() => {
    if (userState.activeTerminal?.id && !canViewInboundFreight) {
      history.replace(Routes.home);
    }
  }, [canViewInboundFreight, history, userState.activeTerminal?.id]);

  if (userState.activeTerminal?.id && !canViewInboundFreight) {
    return null;
  }

  return <>
    <ContentContainer
      title="Inbound Freight"
      tooltip={"This module is in Preview. Certain data and features may be incomplete or under development."}
      status={"PREVIEW"}
    >
      <div
        className={`inbound-freight__content${isAnyOverlayOpen ? " inbound-freight__content--disabled" : ""}`}
      >
        <Filters
          searchString={searchString}
          setSearchString={setSearchString}
          status={status}
          setStatus={setStatus}
          isLoading={isLoading}
          terminalOptions={terminalOptions}
          setTerminal={setTerminal}
          terminal={terminal}
          setSubmittedSearch={setSubmittedSearch}
          trailerSearch={trailerSearch}
          setTrailerSearch={setTrailerSearch}
          etaFrom={etaFrom}
          setEtaFrom={setEtaFrom}
          etaTo={etaTo}
          setEtaTo={setEtaTo}
          onSearch={handleSearch}
          onClear={handleClear}
        />
        <SummaryTiles isLoading={isLoadingInboundFreightCounts} inboundFreightCounts={inboundFreightCounts} />
        <div className='inbound-freight__table'>
          <Table
            columns={getInboundFreightsColumn(
              (rowData: any) => {
                if (isAnyOverlayOpen) {
                  return;
                }
                setSelectedFreightForDoor(rowData);
                setAssignDoorDrawerOpen(true);
              },
              (rowData: any) => {
                if (isAnyOverlayOpen) {
                  return;
                }
                setSelectedFreightForAction(rowData);
                setArriveLoadModalOpen(true);
              },
              (rowData: any) => {
                if (isAnyOverlayOpen) {
                  return;
                }
                setSelectedFreightForAction(rowData);
                setCloseDoorModalOpen(true);
              },
              (rowData: any) => {
                if (isAnyOverlayOpen) {
                  return;
                }
                setSelectedFreightForManifest(rowData);
                setManifestDetailsSlideInOpen(true);
              },
            )}
            data={inboundFreights ?? []}
            isLoading={isLoading}
            rowHeight={56}
            minTableHeight={420}
            infiniteScroll
            responsive
          />
        </div>
      </div>
      <AssignDoorDrawer
        isOpen={assignDoorDrawerOpen}
        onClose={() => {
          setAssignDoorDrawerOpen(false);
          setSelectedFreightForDoor(null);
        }}
        manifestNumber={selectedFreightForDoor?.manifestNumber}
        trailerNumber={selectedFreightForDoor?.trailerNumber}
        currentDoorNumber={selectedFreightForDoor?.assignedDoorCode || selectedFreightForDoor?.doorNumber}
        freightType={selectedFreightForDoor?.type}
        freightStatus={selectedFreightForDoor?.status}
        originCity={selectedFreightForDoor?.originCity}
        destinationTerminalName={
          terminalSummaryState.terminallist?.find(
            (t: any) => t.terminalNumber === userState.activeTerminal?.id
          )?.terminalName
        }
        isSaving={isAssignDoorSaving}
        onSave={async (doorNumber, priority) => {
          const terminalNumber = userState.activeTerminal?.id;
          const manifestNumber = selectedFreightForDoor?.manifestNumber;
          const trailerNumber = selectedFreightForDoor?.trailerNumber;
          if (!terminalNumber || !manifestNumber) {
            return;
          }

          const normalizedDoorCode = doorNumber
            ? doorNumber.replace(/^D-?/i, "").replace(/-/g, "")
            : doorNumber;

          try {
            setIsAssignDoorSaving(true);
            await apiQueryAdapter.query(
              `/inbound-freight/${manifestNumber}/assign-door`,
              "POST",
              {
                params: { terminalNumber },
                body: {
                  doorCode: normalizedDoorCode,
                  trailerNumber: trailerNumber ?? undefined,
                  priority: priority ?? 0,
                },
              }
            );
            toast.success("Door assigned successfully!", { autoClose: 3000 });
            queryClient.setQueriesData(
              { queryKey: [QUERY_KEYS.INBOUND_FREIGHT_BY_TERMINAL] },
              (oldData: any) => {
                if (!Array.isArray(oldData)) return oldData;
                return oldData.map((item: any) => {
                  if (item?.manifestNumber?.toString() !== manifestNumber?.toString()) {
                    return item;
                  }
                  return {
                    ...item,
                    assignedDoorCode: normalizedDoorCode,
                    assignedDoorDescription: item?.assignedDoorDescription ?? "At Door",
                  };
                });
              }
            );
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.INBOUND_FREIGHT_BY_TERMINAL],
            });
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.INBOUND_FREIGHT_COUNTS],
            });
            setAssignDoorDrawerOpen(false);
            setSelectedFreightForDoor(null);
          } catch (error) {
            const message =
              (error as any)?.response?.data?.errors?.[0]?.message ||
              (error as any)?.response?.data?.message ||
              (error as any)?.response?.data?.error ||
              (error as any)?.message ||
              "Failed to assign door.";
            toast.error(message, { autoClose: 3000 });
          } finally {
            setIsAssignDoorSaving(false);
          }
        }}
      />
      <ArriveLoadModal
        isOpen={arriveLoadModalOpen}
        onClose={() => {
          setArriveLoadModalOpen(false);
          setSelectedFreightForAction(null);
        }}
        manifestNumber={selectedFreightForAction?.manifestNumber}
        trailerNumber={selectedFreightForAction?.trailerNumber}
        destinationTerminalTimezone={
          terminalSummaryState.terminallist?.find(
            (t: any) => t.terminalNumber === userState.activeTerminal?.id
          )?.timezone
        }
        isSubmitting={isArriveLoadSubmitting}
        originTerminal={
          selectedFreightForAction?.originCity
            ? (() => {
                const terminal = terminalSummaryState.terminallist?.find(
                  (t: any) => t.terminalName?.toUpperCase() === selectedFreightForAction.originCity?.toUpperCase()
                );
                return terminal
                  ? `${terminal.airportCode} - ${terminal.terminalName} ${terminal.terminalNumber}`
                  : undefined;
              })()
            : undefined
        }
        onConfirm={async (values) => {
          const terminalNumber = userState.activeTerminal?.id;
          const manifestNumber = selectedFreightForAction?.manifestNumber;
          if (!terminalNumber || !manifestNumber) {
            return;
          }

          try {
            setIsArriveLoadSubmitting(true);
            await apiQueryAdapter.query(
              `/inbound-freight/${manifestNumber}/arrive-load`,
              "POST",
              {
                params: { terminalNumber },
                body: buildArriveLoadPayload(values),
              }
            );
            toast.success("Load arrived successfully!", { autoClose: 3000 });
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.INBOUND_FREIGHT_BY_TERMINAL],
            });
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.INBOUND_FREIGHT_COUNTS],
            });
            setArriveLoadModalOpen(false);
            setSelectedFreightForAction(null);
          } catch (error) {
            const message =
              (error as any)?.response?.data?.errors?.[0]?.message ||
              (error as any)?.response?.data?.message ||
              (error as any)?.response?.data?.error ||
              (error as any)?.message ||
              "Failed to arrive load.";
            toast.error(message, { autoClose: 3000 });
          } finally {
            setIsArriveLoadSubmitting(false);
          }
        }}
      />
      <ManifestDetailsSlideIn
        isOpen={manifestDetailsSlideInOpen}
        onClose={() => {
          setManifestDetailsSlideInOpen(false);
          setSelectedFreightForManifest(null);
        }}
        freight={selectedFreightForManifest}
        destinationTerminalName={
          userState.activeTerminal?.id
            ? (() => {
                const terminal = terminalSummaryState.terminallist?.find(
                  (t: any) => t.terminalNumber === userState.activeTerminal?.id
                );
                return terminal
                  ? `${terminal.airportCode} - ${terminal.terminalName} ${terminal.terminalNumber}`
                  : undefined;
              })()
            : undefined
        }
      />
      <CloseDoorModal
        isOpen={closeDoorModalOpen}
        onClose={() => {
          setCloseDoorModalOpen(false);
          setSelectedFreightForAction(null);
        }}
        manifestNumber={selectedFreightForAction?.manifestNumber}
        doorNumber={selectedFreightForAction?.assignedDoorCode || selectedFreightForAction?.doorNumber}
        destinationTerminal={
          userState.activeTerminal?.id
            ? (() => {
                const terminal = terminalSummaryState.terminallist?.find(
                  (t: any) => t.terminalNumber === userState.activeTerminal?.id
                );
                return terminal
                  ? `${terminal.airportCode} - ${terminal.terminalName} ${terminal.terminalNumber}`
                  : undefined;
              })()
            : undefined
        }
        pieces={selectedFreightForAction?.piecesCount}
        piecesUnloaded={selectedFreightForAction?.piecesUnloaded}
        weight={selectedFreightForAction?.lbs}
        isSubmitting={isCloseDoorSubmitting}
        onConfirm={async () => {
          const terminalNumber = userState.activeTerminal?.id;
          const manifestNumber = selectedFreightForAction?.manifestNumber;
          const doorCode = selectedFreightForAction?.assignedDoorCode || selectedFreightForAction?.doorNumber;
          if (!terminalNumber || !manifestNumber || !doorCode) {
            return;
          }

          const normalizedDoorCode = doorCode
            ? doorCode.replace(/^D-?/i, "").replace(/-/g, "")
            : doorCode;

          try {
            setIsCloseDoorSubmitting(true);
            await apiQueryAdapter.query(
              `/inbound-freight/${manifestNumber}/close-door`,
              "POST",
              {
                params: { terminalNumber },
                body: {
                  doorCode: normalizedDoorCode,
                  trailerNumber: selectedFreightForAction?.trailerNumber ?? undefined,
                },
              }
            );
            toast.success("Door closed successfully!", { autoClose: 3000 });
            queryClient.setQueriesData(
              { queryKey: [QUERY_KEYS.INBOUND_FREIGHT_BY_TERMINAL] },
              (oldData: any) => {
                if (!Array.isArray(oldData)) return oldData;
                return oldData.map((item: any) => {
                  if (item?.manifestNumber?.toString() !== manifestNumber?.toString()) {
                    return item;
                  }
                  return {
                    ...item,
                    assignedDoorCode: null,
                    assignedDoorDescription: null,
                    assignedLocationDescription: null,
                  };
                });
              }
            );
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.INBOUND_FREIGHT_BY_TERMINAL],
            });
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.INBOUND_FREIGHT_COUNTS],
            });
            setCloseDoorModalOpen(false);
            setSelectedFreightForAction(null);
          } catch (error) {
            const message =
              (error as any)?.response?.data?.errors?.[0]?.message ||
              (error as any)?.response?.data?.message ||
              (error as any)?.response?.data?.error ||
              (error as any)?.message ||
              "Failed to close door.";
            toast.error(message, { autoClose: 3000 });
          } finally {
            setIsCloseDoorSubmitting(false);
          }
        }}
      />
    </ContentContainer>
  </>;
};

export default InboundFreights;
